/**
 * Ryan O'Dowd
 * 2024-07-28
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import SearchFilterSection from './SearchFilterSection';
import Text from '@components/common/Text';
import TranslationSelectionDialog from '@components/TranslationSelectionDialog';
import getStyles from './styles';
import hooks from '@hooks';

const SearchFilterDialog = (props: SearchFilterDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  const [_isTranslationMenuOpen, setIsTranslationMenuOpen] = useState(false);

  const userSettings = hooks.useTypedSelector((state) => state.userSettings);
  const selectedTranslation = hooks.useTypedSelector((state) => state.translations.find((t) => t.id === userSettings.preferred_translation_id), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue)); // @TODO:  && t.locale_id === (state.oakUser.locale_id || RNLocalize.getLocales().map((lo) => lo.languageCode).filter((lc) => ['en', 'es'].includes(lc)).shift() || 'en'))); // @TODO: locale_id is now in userSettings, not oakUser

  const translationTouchableText = (isDisabled) => (
    <Text
      style={[
        styles.translationText,
        isDisabled && styles.disabledText,
      ]}
      disabled={isDisabled}// @TODO: not working...should this be a touchable instead of a text?
      onPress={() => {
        if (!isDisabled) {
          setIsTranslationMenuOpen(true);
        }
      }}
    >
      {selectedTranslation.abbreviation}
    </Text>
  );

  return (
    <Dialog
      visible={props.isOpen}
      onDismiss={() => props.onClose()}
    >
      <Dialog.Title>{i18n('advancedSearch')}</Dialog.Title>
      <Dialog.Content>
        <SearchFilterSection
          sectionName={i18n('goals')}
          settingColumnName='search_goals_on'
          enabled={userSettings.search_goals_on}
          subFilters={[
            {
              readableName: 'Search my goals',
              columnName: 'search_goals_my_goals_on',
              value: userSettings.search_goals_my_goals_on,
            },
            {
              readableName: 'Search for new goals',
              columnName: 'search_goals_discover_on',
              value: userSettings.search_goals_discover_on,
            },
          ]}
        />
        <SearchFilterSection
          sectionName={i18n('verseTexts')}
          settingColumnName='search_verse_texts_on'
          enabled={userSettings.search_verse_texts_on}
          subFilters={[
            {
              readableName: <Text>Search {translationTouchableText(!userSettings.search_verse_texts_on)} only</Text>,// @TODO: i18n
              columnName: 'search_verse_texts_preferred_translation_only',
              value: userSettings.search_verse_texts_preferred_translation_only,
              description: i18n('superSearchVerseTextsDescription'),
            },
          ]}
        />
        <SearchFilterSection
          sectionName={i18n('friends')}
          settingColumnName='search_friends_on'
          enabled={userSettings.search_friends_on}
          subFilters={[
            {
              readableName: 'Search users following me',
              columnName: 'search_friends_my_followers_on',
              value: userSettings.search_friends_my_followers_on,
            },
            {
              readableName: 'Search users I follow',
              columnName: 'search_friends_my_following_on',
              value: userSettings.search_friends_my_following_on,
            },
            {
              readableName: 'Search all other users',
              columnName: 'search_friends_new_friends_on',
              value: userSettings.search_friends_new_friends_on,
            },
          ]}
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          mode='contained'
          onPress={() => props.onClose()}
        >
          {i18n('done')}
        </Button>
      </Dialog.Actions>
      <TranslationSelectionDialog
        isOpen={_isTranslationMenuOpen}
        onClose={() => setIsTranslationMenuOpen(false)}
        autoClose={true}
        closeButtonText={i18n('close')}
      />
    </Dialog>
  );
};

class SearchFilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
};

export default SearchFilterDialog;
